import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Shield, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const hrs = Math.round(diffMs / 3_600_000);
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

/**
 * Compact "Last backup" pill that the CEO can see at a glance on her home
 * dashboard. Hits /api/sitevault/status (CEO-only) so it's invisible to other
 * roles.
 */
export default function BackupStatusStrip({ className = '' }) {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/sitevault/status');
        setStatus(r.data);
      } catch (e) {
        if (e?.response?.status === 403) { setErr('not-ceo'); return; }
        setErr(e?.response?.data?.detail || 'Backup status unavailable');
      }
    })();
  }, []);

  if (err === 'not-ceo') return null;
  if (!status && !err) {
    return (
      <div className={`text-[11px] py-2 px-3 ${className}`} style={{ color: '#9CA3AF' }}>
        Checking backup status…
      </div>
    );
  }
  if (err) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded border text-[11px] ${className}`}
        style={{ borderColor: '#FEE2E2', backgroundColor: '#FEF2F2', color: '#991B1B' }}
        data-testid="backup-status-strip-error">
        <AlertTriangle size={12} /> {err}
      </div>
    );
  }

  const last = status.last_job;
  const lastSuccess = status.last_successful_job || (last && last.status === 'done' ? last : null);
  const startedAt = last?.started_at;
  const successStartedAt = lastSuccess?.started_at;
  const isMock = status.mock_mode;

  // Compute hours since the last SUCCESSFUL backup (not the last attempted).
  // A stuck "running" or "failed" job does not count as healthy.
  const hoursSinceSuccess = successStartedAt
    ? (Date.now() - new Date(successStartedAt).getTime()) / 3_600_000
    : Infinity;

  // Health states (strict):
  //   HEALTHY  — last successful run in the last 30 hours
  //   STALE    — 30-36h since last successful run (amber warning)
  //   CRITICAL — >36h since last successful run (red, shouts at CEO)
  //   UNKNOWN  — no jobs yet
  let state;
  if (!lastSuccess) state = 'UNKNOWN';
  else if (hoursSinceSuccess > 36) state = 'CRITICAL';
  else if (hoursSinceSuccess > 30) state = 'STALE';
  else state = 'HEALTHY';

  const palette = {
    HEALTHY:  { border: '#D1FAE5', bg: '#F0FDF4', icon: '#059669' },
    STALE:    { border: '#FDE68A', bg: '#FFFBEB', icon: '#B7791F' },
    CRITICAL: { border: '#FECACA', bg: '#FEF2F2', icon: '#DC2626' },
    UNKNOWN:  { border: '#E5E7EB', bg: '#F9FAFB', icon: '#6B7280' },
  }[state];

  const stuckRunning = last?.status === 'running';

  return (
    <div className={`flex flex-wrap items-center gap-3 px-3 py-2 rounded border text-[11.5px] ${className}`}
      style={{ borderColor: palette.border, backgroundColor: palette.bg, color: '#1B2A4A' }}
      data-testid="backup-status-strip" data-state={state}>
      <Shield size={14} style={{ color: palette.icon }} />
      <span className="font-semibold">SiteVault</span>
      <span style={{ color: '#6B7280' }}>·</span>
      {lastSuccess ? (
        <>
          <span>
            Last successful {lastSuccess.scope || 'backup'}: <strong>{relTime(successStartedAt)}</strong>
            {lastSuccess.total_size_mb != null && (
              <span style={{ color: '#9CA3AF' }}> · {lastSuccess.total_size_mb.toFixed(0)} MB</span>
            )}
          </span>
          <span style={{ color: '#9CA3AF' }}>·</span>
          {state === 'HEALTHY' && (
            <span className="inline-flex items-center gap-1" style={{ color: '#059669' }}>
              <CheckCircle2 size={11} /> healthy
            </span>
          )}
          {state === 'STALE' && (
            <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#B7791F' }}>
              <AlertTriangle size={11} /> stale ({Math.round(hoursSinceSuccess)}h)
            </span>
          )}
          {state === 'CRITICAL' && (
            <span className="inline-flex items-center gap-1 font-bold" style={{ color: '#DC2626' }}>
              <AlertTriangle size={11} /> CRITICAL · {Math.round(hoursSinceSuccess)}h since last backup
            </span>
          )}
        </>
      ) : (
        <span style={{ color: '#9CA3AF' }}>No successful backups yet.</span>
      )}
      {stuckRunning && (
        <>
          <span style={{ color: '#9CA3AF' }}>·</span>
          <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#B7791F' }}>
            <AlertTriangle size={11} /> a backup is stuck in progress ({relTime(startedAt)})
          </span>
        </>
      )}
      <span style={{ color: '#9CA3AF' }}>·</span>
      <span style={{ color: '#6B7280' }}>
        Next auto: <strong style={{ color: '#1B2A4A' }}>Mon 03:00 IST full</strong>
        <span className="mx-1">·</span>
        <strong style={{ color: '#1B2A4A' }}>daily 04:00 IST DB</strong>
      </span>
      {isMock && (
        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          MOCK MODE
        </span>
      )}
      <a href="/admin" className="ml-auto inline-flex items-center gap-1 hover:underline" style={{ color: '#2A7B88' }}>
        Manage <ExternalLink size={10} />
      </a>
    </div>
  );
}
